import { mean } from 'es-toolkit/compat';

import {
    DiagnosticRating,
    INCONSISTENCIES_RATING_THRESHOLD,
    isNotNil,
    KEYWORDS_RATING_THRESHOLD,
    type MaloupeLocale,
    PHOTOS_RATING_THRESHOLD,
    ReviewAnalysisSentiment,
    roundToDecimals,
    SCORE_RATING,
} from '@malou-io/package-utils';

import type {
    DetailedInconsistenciesSectionData,
    DetailedKeywordsSectionData,
    DetailedSemanticAnalysisSectionData,
    DetailedSeoSectionData,
    DetailedSocialMediaSectionData,
    SharedInstagramData,
} from ':shared/interfaces';

import { MINIMUM_REVIEWS_WITH_TEXT } from ':shared/constants';
import { MaloupePlatformKey } from ':shared/enums';
import { type Diagnostic, ScoreCriterions } from ':shared/models';

export class AggregatedDiagnostics {
    diagnostics: Diagnostic[];
    diagnosticsScoreCriterions: ScoreCriterions[];

    readonly MAX_PLATFORM_COUNT_TO_DISPLAY = 2;

    constructor(diagnostics: Diagnostic[]) {
        this.diagnostics = diagnostics;
        this.diagnosticsScoreCriterions = diagnostics.map((diagnostic) => new ScoreCriterions(diagnostic));
    }

    getDiagnosticsData() {
        return this.diagnostics
            .map((diagnostic) => ({
                id: diagnostic.id,
                restaurantName: diagnostic.restaurant.name,
                restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? null,
                instagramPage: diagnostic.instagramPage ? diagnostic.instagramPage.userName : null,
                instagramPageProfile: diagnostic.instagramPage ? diagnostic.instagramPage.profilePictureUrl : null,
                totalScore: diagnostic.getTotalScore(),
                totalScoreRating: diagnostic.getTotalScoreRating(),
                scoreGaugeImage: this._getScoreGaugeImageByScore(diagnostic.getTotalScore()),
            }))
            .sort((a, b) => b.totalScore - a.totalScore);
    }

    // ------------------- Score -------------------
    getTotalScore(): number {
        return roundToDecimals(Math.round(mean(this.diagnostics.map((diagnostic) => diagnostic.getTotalScore()))), 0);
    }

    getTotalScoreRating(): DiagnosticRating {
        const score = this.getTotalScore();
        if (score < SCORE_RATING.MINIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.BAD;
        }
        if (score < SCORE_RATING.MINIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.GOOD;
    }

    getTotalScoreGaugeImage(): string {
        return this._getScoreGaugeImageByScore(this.getTotalScore());
    }

    getRestaurantScoreCriterions(diagnosticId: string): ScoreCriterions {
        return this.diagnosticsScoreCriterions.find((scoreCriterion) => scoreCriterion.diagnosticId === diagnosticId);
    }

    // ------------------- Inconsistencies -------------------
    getAggregatedInconsistencies(): {
        platformCount: number;
        inconsistencyCount: number;
        platformKeys: string[];
    } | null {
        const inconsistencies = this.diagnostics
            .filter((diagnostic) => diagnostic.inconsistencies)
            .map((diagnostic) => diagnostic.inconsistencies);
        if (inconsistencies.length === 0) {
            return null;
        }
        const inconsistencyCount = inconsistencies.reduce((acc, inconsistency) => acc + inconsistency?.inconsistencyCount, 0);
        const platformKeys = Array.from(
            new Set(inconsistencies.flatMap((inconsistency) => Object.keys(inconsistency?.platformKeys ?? {})))
        );
        return { inconsistencyCount, platformKeys, platformCount: platformKeys.length };
    }

    getInconsistencyRating(): DiagnosticRating {
        const inconsistenciesRate = this.diagnostics.map((diagnostic) => diagnostic.getInconsistencyRate());
        if (inconsistenciesRate.length === 0) {
            return DiagnosticRating.BAD;
        }
        const sumInconsistenciesRate = inconsistenciesRate.reduce((acc, rate) => acc + rate, 0);
        if (sumInconsistenciesRate < inconsistenciesRate.length * INCONSISTENCIES_RATING_THRESHOLD.MAXIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.GOOD;
        }
        if (sumInconsistenciesRate < inconsistenciesRate.length * INCONSISTENCIES_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.BAD;
    }

    getInconsistenciesDataPerRestaurant(): DetailedInconsistenciesSectionData[] {
        return this.diagnostics.map((diagnostic) => ({
            diagnosticId: diagnostic.id,
            restaurantName: diagnostic.restaurant.name,
            restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? null,
            inconsistencies: {
                count: diagnostic.inconsistencies?.inconsistencyCount,
                total: diagnostic.inconsistencies?.platformCount,
                rating: diagnostic.getInconsistencyRating(),
                platforms: this._getPlatformsToDisplay(
                    diagnostic.inconsistencies?.platformKeys,
                    diagnostic.inconsistencies?.platformCount ?? 0
                ),
            },
            photos: {
                count: diagnostic.photoCount,
                total: PHOTOS_RATING_THRESHOLD.MINIMUM_GOOD_THRESHOLD,
                rating: diagnostic.getPhotosRating(),
            },
            attributes: {
                count: diagnostic.getPresentServiceCount(),
                total: diagnostic.getServiceCount(),
                rating: diagnostic.getServicesRating(),
            },
        }));
    }

    // ------------------- Keywords ------------------

    getKeywordsRating(): DiagnosticRating {
        const keywordsAverageRatings = this.diagnostics.map((diagnostic) => diagnostic.getKeywordsAverageRating());
        if (keywordsAverageRatings.length === 0) {
            return DiagnosticRating.BAD;
        }
        const sumKeywordsAverageRatings = keywordsAverageRatings.reduce((acc, rate) => acc + rate, 0);
        if (sumKeywordsAverageRatings < keywordsAverageRatings.length * KEYWORDS_RATING_THRESHOLD.MAXIMUM_GOOD_THRESHOLD) {
            return DiagnosticRating.GOOD;
        }
        if (sumKeywordsAverageRatings < keywordsAverageRatings.length * KEYWORDS_RATING_THRESHOLD.MAXIMUM_AVERAGE_THRESHOLD) {
            return DiagnosticRating.AVERAGE;
        }
        return DiagnosticRating.BAD;
    }

    getKeywordsDataPerRestaurant(locale: MaloupeLocale): DetailedKeywordsSectionData[] {
        return this.diagnostics
            .map((diagnostic) => ({
                diagnosticId: diagnostic.id,
                restaurantName: diagnostic.restaurant.name,
                restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? null,
                keywordsRating: diagnostic.getKeywordsRating(),
                keywords: diagnostic.getKeywords().map((keyword) => ({
                    name: keyword.keyword[locale] ?? undefined,
                    googlePosition: keyword.googlePosition,
                    competitors: keyword.competitorsGooglePositions,
                })),
            }))
            .filter((data) => data.keywords.length > 0);
    }

    // ------------------- Google  -------------------

    getSeoAggregatedGoogleRating(): number {
        return roundToDecimals(mean(this.diagnostics.map((diagnostic) => diagnostic.restaurant.rating)), 1);
    }

    getRestaurantsWithHigherRatingCount(): number {
        return this.diagnostics.filter(
            (diagnostic) =>
                diagnostic.restaurant.rating &&
                diagnostic.restaurant.rating < (diagnostic.restaurant.averageRatingForSimilarRestaurants ?? 0)
        ).length;
    }

    getSeoRatingsDataPerRestaurant(): DetailedSeoSectionData[] {
        return this.diagnostics.map((diagnostic) => ({
            diagnosticId: diagnostic.id,
            restaurantName: diagnostic.restaurant.name,
            restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? null,
            reviewRating: diagnostic.getReviewCountRating() ?? DiagnosticRating.BAD,
            googleRating: diagnostic.getGoogleRating() ?? DiagnosticRating.BAD,
            rating: diagnostic.restaurant.rating ?? null,
            reviewCount: diagnostic.reviewCount,
            averageReviewCountForSimilarRestaurants: diagnostic.averageReviewCountForSimilarRestaurants,
            averageRatingForSimilarRestaurants: diagnostic.restaurant.averageRatingForSimilarRestaurants,
        }));
    }

    // ------------------- Reviews -------------------
    getAggregatedSemanticAnalysisData(): DetailedSemanticAnalysisSectionData {
        const sentimentCountPerRestaurant = this.diagnostics.map((diagnostic) => {
            const reviews = diagnostic.reviews ?? [];
            const segments = reviews
                .flatMap((review) => review.semanticAnalysisSegments)
                .filter((segment) => {
                    const isNeutral = segment.sentiment === ReviewAnalysisSentiment.NEUTRAL;
                    return !isNeutral;
                });

            if (segments.length === 0) {
                return {
                    positiveSentimentsCount: 0,
                    negativeSentimentsCount: 0,
                    count: 0,
                    restaurantName: diagnostic.restaurant.name,
                    restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? '-',
                };
            }

            const sentimentCount = segments.length;
            const positiveSentimentsCount = segments.filter((sentiment) => sentiment.sentiment === ReviewAnalysisSentiment.POSITIVE).length;

            const negativeSentimentsCount = segments.filter((sentiment) => sentiment.sentiment === ReviewAnalysisSentiment.NEGATIVE).length;

            return {
                positiveSentimentsCount,
                negativeSentimentsCount,
                count: sentimentCount,
                restaurantName: diagnostic.restaurant.name,
                restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? '-',
            };
        });

        const totalPositiveSentimentsCount = sentimentCountPerRestaurant.reduce(
            (acc, sentiment) => acc + sentiment.positiveSentimentsCount,
            0
        );
        const totalNegativeSentimentsCount = sentimentCountPerRestaurant.reduce(
            (acc, sentiment) => acc + sentiment.negativeSentimentsCount,
            0
        );
        const totalSentimentCount = sentimentCountPerRestaurant.reduce((acc, sentiment) => acc + sentiment.count, 0);

        return {
            sentimentsPercentagesPerRestaurant: sentimentCountPerRestaurant.map((sentimentCount) => ({
                positiveSentimentsPercentage: this._getPercentage(sentimentCount.positiveSentimentsCount, sentimentCount.count),
                negativeSentimentsPercentage: this._getPercentage(sentimentCount.negativeSentimentsCount, sentimentCount.count),
                sentimentCount: sentimentCount.count,
                restaurantName: sentimentCount.restaurantName,
                restaurantAddress: sentimentCount.restaurantAddress,
            })),
            aggregated: {
                positiveSentimentsPercentage: this._getPercentage(totalPositiveSentimentsCount, totalSentimentCount),
                positiveSentimentCount: totalPositiveSentimentsCount,
                negativeSentimentsPercentage: this._getPercentage(totalNegativeSentimentsCount, totalSentimentCount),
                negativeSentimentCount: totalNegativeSentimentsCount,
                sentimentCount: totalSentimentCount,
            },
        };
    }

    getReviewsPerRestaurant(): Diagnostic['reviews'][] {
        return this.diagnostics
            .map((diagnostic) => diagnostic.reviews ?? [])
            .filter((reviews) => reviews.length >= MINIMUM_REVIEWS_WITH_TEXT);
    }

    getRandomSemanticAnalysisOverviewByLang(lang: MaloupeLocale): string | null {
        const semanticAnalysisOverviewsByLang = this.diagnostics
            .map((diagnostic) => diagnostic.semanticAnalysisOverviewByLang?.[lang] ?? null)
            .filter((overview) => overview !== null);
        if (semanticAnalysisOverviewsByLang.length === 0) {
            return null;
        }
        return semanticAnalysisOverviewsByLang[Math.floor(Math.random() * semanticAnalysisOverviewsByLang.length)];
    }

    // ------------------- Social Media --------------
    isSameInstagramAccountForAllRestaurants(): boolean {
        const instagramAccounts = this.diagnostics.map((diagnostic) => diagnostic.instagramPage?.name);
        return new Set(instagramAccounts).size === 1;
    }

    getSharedInstagramAccountData(): SharedInstagramData | null {
        const diagnosticWithValidInstagramAccount = this.diagnostics.find((diagnostic) => isNotNil(diagnostic.instagramPage));
        if (!diagnosticWithValidInstagramAccount) {
            return null;
        }
        const instagramAccount = diagnosticWithValidInstagramAccount.instagramPage;
        const averageFollowerCountForSimilarRestaurants = this.diagnostics
            .map((diagnostic) => diagnostic.instagramPage?.averageFollowerCountForSimilarRestaurants ?? null)
            .filter(isNotNil);
        const averageLikeCountForSimilarRestaurants = this.diagnostics
            .map((diagnostic) => diagnostic.instagramPage?.averageLikeCountForSimilarRestaurants ?? null)
            .filter(isNotNil);
        const averagePostCountForSimilarRestaurants = this.diagnostics
            .map((diagnostic) => diagnostic.instagramPage?.averagePostCountForSimilarRestaurants ?? null)
            .filter(isNotNil);
        return {
            instagramPage: {
                ...instagramAccount,
                averageFollowerCountForSimilarRestaurants: roundToDecimals(mean(averageFollowerCountForSimilarRestaurants), 0),
                averageLikeCountForSimilarRestaurants: roundToDecimals(mean(averageLikeCountForSimilarRestaurants), 0),
                averagePostCountForSimilarRestaurants: roundToDecimals(mean(averagePostCountForSimilarRestaurants), 0),
            },
            instagramRating: diagnosticWithValidInstagramAccount.getInstagramRating(),
        };
    }

    getSocialMediaDataPerRestaurant(): DetailedSocialMediaSectionData[] {
        const detailedSocialMediaPerRestaurant = this.diagnostics.map((diagnostic) => {
            if (!diagnostic.instagramPage) {
                return null;
            }
            const instagramRating = diagnostic.getInstagramRating();
            const isPerformingBetterThanCompetitors =
                [
                    instagramRating.followersDiagnosticRating,
                    instagramRating.likesDiagnosticRating,
                    instagramRating.postCountDiagnosticRating,
                ].filter((rating) => rating === DiagnosticRating.GOOD).length >= 2;
            return {
                diagnosticId: diagnostic.id,
                restaurantName: diagnostic.restaurant.name,
                restaurantAddress: diagnostic.restaurant.address.formattedAddress ?? null,
                followerCount: diagnostic.instagramPage?.followerCount ?? 0,
                isPerformingBetterThanCompetitors,
                averageLikeCount: roundToDecimals(diagnostic?.instagramPage?.averageLikeCount ?? 0, 0),
                postCount: diagnostic?.instagramPage?.postCount ?? 0,
                averageCompetitorFollowerCount: roundToDecimals(
                    diagnostic.instagramPage?.averageFollowerCountForSimilarRestaurants ?? 0,
                    0
                ),
                averageCompetitorAverageLikes: roundToDecimals(diagnostic.instagramPage?.averageLikeCountForSimilarRestaurants ?? 0, 0),
                averageCompetitorPostCount: roundToDecimals(diagnostic.instagramPage?.averagePostCountForSimilarRestaurants ?? 0, 0),
            };
        });
        return detailedSocialMediaPerRestaurant.filter(isNotNil);
    }

    // ------------------- Private -------------------

    private _getScoreGaugeImageByScore(score: number): string {
        if (score < 10) {
            return 'gauge-10';
        }
        if (score > 90) {
            return 'gauge-90';
        }
        const images = ['gauge-10', 'gauge-20', 'gauge-30', 'gauge-40', 'gauge-50', 'gauge-60', 'gauge-70', 'gauge-80', 'gauge-90'];
        const index = Math.floor(score / 10) - 1;
        return images[index];
    }

    private _getPercentage(value: number | null, total: number): number {
        if (total === 0 || value === null) {
            return 0;
        }
        return roundToDecimals((value * 100) / total, 0);
    }

    private _getPlatformsToDisplay(
        platformKeys: string[] | undefined,
        platformCount: number
    ): DetailedInconsistenciesSectionData['inconsistencies']['platforms'] {
        if (!platformKeys) {
            return {
                platformsToDisplay: [],
                otherPlatformCount: 0,
            };
        }

        const platformKeysToLowerCase = platformKeys.map((key) => key.toLowerCase());
        const prioritizedPlatforms = [MaloupePlatformKey.GMB, MaloupePlatformKey.APPLE].filter((key) =>
            platformKeysToLowerCase.includes(key)
        );

        if (prioritizedPlatforms.length === this.MAX_PLATFORM_COUNT_TO_DISPLAY) {
            return {
                platformsToDisplay: prioritizedPlatforms,
                otherPlatformCount: platformCount - this.MAX_PLATFORM_COUNT_TO_DISPLAY,
            };
        }

        const platformsToDisplay: string[] = [
            MaloupePlatformKey.FACEBOOK,
            MaloupePlatformKey.TRIPADVISOR,
            MaloupePlatformKey.FOURSQUARE,
            MaloupePlatformKey.BING,
        ];
        const displayedPlatforms = [...prioritizedPlatforms, ...platformsToDisplay.filter((key) => platformKeysToLowerCase.includes(key))];

        if (platformCount < this.MAX_PLATFORM_COUNT_TO_DISPLAY) {
            return {
                platformsToDisplay: displayedPlatforms.slice(0, this.MAX_PLATFORM_COUNT_TO_DISPLAY),
                otherPlatformCount: 0,
            };
        }

        return {
            platformsToDisplay: displayedPlatforms.slice(0, this.MAX_PLATFORM_COUNT_TO_DISPLAY),
            otherPlatformCount: platformCount - this.MAX_PLATFORM_COUNT_TO_DISPLAY,
        };
    }
}
